import * as React from "react";
import { toast } from "react-toastify";
import {
  useHistory,
  useRouteMatch
} from "react-router-dom";
import moment from "moment-timezone";
import _ from "lodash";

import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import { stripHtml } from "src/library/utils/string-manipulations";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { OrderAdminMarkAsManuallyProcessedModal } from "src/core/orders/components/Modals";
import CancelOrderModal from "src/core/orders/components/Modals/CancelOrderModal";
import ListFromOrderModal from "src/core/orders/components/Modals/ListFromOrderModal";
import {
  canMarkOrderAsManuallyProcessed,
  canResyncOrder,
  isOrderViewOnly
} from "src/core/orders/utils/orders";
import {
  getOrderStatusIdFromCode,
  getOrderStatusNameFromCode
} from "src/core/orders/utils/orderStatus";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import { useOrder } from "src/core/orders/hooks";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { useCurrentUser } from "src/core/users/hooks";
import { useCurrentOrderValidation } from "src/context/order-validation-context";
import {
  IOrder,
  OrderNoteSourceEnum,
  ordersApi
} from "src/core/orders/api/orders.api";
import { IRole } from "src/core/shared/api/roles.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { ERPRJL } from "src/utils/constants";

import { IOrderNote } from "../api/orders.api";
import { DuplicateOrderModal } from "./Modals/DuplicateOrderModal";
import { AddInvoiceNumberModal } from "./Modals/AddInvoiceNumberModal";

interface OrderActionsListProps {
  order?: IOrder;
  role: IRole;
  terminology: ITerminology;
  erp: ERPType;
  reload?: () => void;
}

export const OrderActionsList: React.ReactFCWithChildren<OrderActionsListProps> = ({
  order, role, erp, terminology, reload
}) => {
  const history = useHistory();
  const match = useRouteMatch<{ orderId?: string }>();
  const orderId = match.params.orderId ? React.useMemo(() => parseInt(match.params.orderId as string, 0), [ match ]) : null;

  const [
    { currentOrder, isSubmittingOrder },
    {
      markAsManuallyProcessed, setOrderMode, resyncOrder, createListFromOrder, refreshOrder, submitOrder
    }
  ] = useOrder(
    orderId,
    order,
    false,
    reload
  );

  const [ { currentUser, currentRole } ] = useCurrentUser();

  const {
    canBeAmended, canBeCancelled, canBeSubmitted
  } = useCurrentOrderValidation();

  const canDuplicate = React.useMemo(() => !!(currentOrder.data && orderId), [ currentOrder.data, orderId ]);
  const isViewOnly = React.useMemo(() => currentOrder.data ? isOrderViewOnly(currentOrder.data) : false, [ currentOrder.data ]);
  const [ listFromOrderModal, setListFromOrderModal ] = React.useState<number | null>(null);
  const [ showDuplicateOrderModal, setShowDuplicateOrderModal ] = React.useState<boolean>(false);
  const [ showAddInvoiceNumberModal, setShowAddInvoiceNumberModal ] = React.useState<boolean>(false);
  const [ cancelOrderModal, setCancelOrderModal ] = React.useState<number | null>(null);
  const [ showMarkOrderAsManuallyProcessedModal, setShowMarkOrderAsManuallyProcessedModal ] = React.useState<boolean>(false);
  const [ cancelOrderResponse, cancelOrderRequest ] = ordersApi<IOrder>("CANCEL_ORDER");
  const [ invoiceOrderResponse, invoiceOrderRequest ] = ordersApi<IOrder>("INVOICE_ORDER");
  const [ { globalSettings } ] = useGlobalSettings();
  const manuallyProcessedStatusName = globalSettings ? getOrderStatusNameFromCode(globalSettings.orderStatuses, "manually_processed") : undefined;
  let actionButtonItems: IAction[] = [];
  const checkUserPermission = useCheckUserPermission();

  // Can an invoice number be added?
  const canAddInvoiceNumber = React.useMemo(() => {
    return (
      globalSettings &&
      globalSettings.orderStatuses &&
      currentOrder.data &&
      currentOrder.data.orderStatus?.id &&
      (currentOrder.data.orderStatus.settings.allowedTransitions.includes("invoiced") || currentOrder.data?.orderStatus?.name.toLowerCase() === "invoiced") &&
      currentOrder.data.orderDeliveryDate &&
      checkUserPermission("amend-order") &&
      match.params.orderId &&
      moment.utc().endOf("day").isAfter(moment(currentOrder.data.orderDeliveryDate)));
  }, [
    globalSettings,
    currentOrder.data,
    checkUserPermission,
    match.params.orderId
  ]);

  /**
   * @description Handles the response from adding an invoice number to an Order.
   */
  React.useEffect(() => {
    // Handle data response
    if (invoiceOrderResponse.data) {
      toast.success(`${_.capitalize(terminology?.order.singular.text ?? "order")} invoice number succesfully updated`);
      refreshOrder();
      invoiceOrderResponse.data = null;
      setShowAddInvoiceNumberModal(false);
    }

    // Handle error response
    if (invoiceOrderResponse.error) {
      toast.error(invoiceOrderResponse.error.message);
      invoiceOrderResponse.error = null;
    }
  }, [
    invoiceOrderResponse,
    order,
    terminology?.order.singular.text,
    refreshOrder
  ]);

  /**
   * Creates a list from the order
   */
  const createListFromOrderButton = React.useCallback(() => {
    if (checkUserPermission("create-list")) {
      if (match.params.orderId && currentOrder.data) {
        const buttonConfig: IAction = {
          title: `Create ${terminology.list.singular.text} from ${terminology.order.singular.text}`,
          action: () => setListFromOrderModal((currentOrder.data as IOrder).id),
          disabled: isViewOnly
        };

        if (isViewOnly) {
          buttonConfig.toolTip = {
            id: currentOrder.data.id.toString(),
            tip: "Cannot create list from this order"
          };
        }

        return [ buttonConfig ];
      }
    }

    return [];
  }, [
    terminology,
    currentOrder.data,
    checkUserPermission,
    isViewOnly,
    match.params.orderId
  ]);

  // Handle the cancel response
  React.useEffect(() => {
    if (cancelOrderResponse.data && refreshOrder) {
      refreshOrder();
    }
  }, [ cancelOrderResponse.data ]);

  /**
   * Duplicates an order for another date
   */
  const duplicateOrderButton = React.useCallback(() => {
    if (canDuplicate && checkUserPermission("amend-order")) {
      const buttonConfig: IAction = {
        title: `Duplicate ${terminology.order.singular.text}`,
        action: () => setShowDuplicateOrderModal(true),
        disabled: isViewOnly
      };

      if (isViewOnly) {
        buttonConfig.toolTip = {
          id: currentOrder.data?.id.toString() || "duplicate",
          tip: "This order cannot be edited, duplicated or used to create lists"
        };
      }

      return [ buttonConfig ];
    }

    return [];
  }, [
    canDuplicate,
    checkUserPermission,
    isViewOnly,
    currentOrder.data?.id,
    terminology.order.singular.text
  ]);

  /**
   * @description Button to add an invoice number to an order
   */
  const updateInvoiceNumberButton = React.useCallback(() => {
    // Only show add invoice number option if memoised conditions are met
    if (canAddInvoiceNumber && globalSettings && currentOrder?.data?.orderStatus) {
      // Fetch Ids for DUE group order statuses
      const dueGroupOrderStatusIds =
        globalSettings.orderStatuses.filter(
          orderStatus => orderStatus.settings.group === "DUE").map(
          orderStatus => orderStatus.id);

      // Only return update invoice number option if the order status belongs to the DUE group
      if (dueGroupOrderStatusIds.includes(currentOrder.data.orderStatus.id)) {
        return [
          {
            title: "Update order invoice number",
            action: () => setShowAddInvoiceNumberModal(true),
            disabled: invoiceOrderResponse.pending
          }
        ];
      }
    }

    return [];
  }, [
    canAddInvoiceNumber,
    currentOrder.data?.orderStatus,
    globalSettings,
    invoiceOrderResponse.pending
  ]);

  /**
   * Amend order button
   */
  const amendOrderButton = React.useCallback(() => {
    if (checkUserPermission("amend-order")) {
      if (match.params.orderId && currentOrder.data) {
        // if canBeAmended is a string, it is the reason why the order cannot be amended
        // and therefore ability to edit should be disabled
        const amendOrderDisabled = typeof canBeAmended === "string" || isViewOnly;

        const buttonConfig: IAction = {
          action: () => {
            if (match.params && match.params.orderId) {
              if (currentOrder.data) {
                setOrderMode("edit");
              }
            } else {
              history.push(`/orders/${(currentOrder.data as IOrder).id}`, { edit: true });
            }
          },
          title: `Edit ${terminology.order.singular.text}`,
          disabled: amendOrderDisabled
        };

        if (amendOrderDisabled) {
          buttonConfig.toolTip = {
            id: currentOrder.data.id.toString(),
            tip: isViewOnly ? "This order cannot be edited" : (canBeAmended as string)
          };
        }

        return [ buttonConfig ];
      }
    }

    return [];
  }, [
    checkUserPermission,
    match.params,
    currentOrder,
    canBeAmended,
    isViewOnly,
    terminology.order.singular.text,
    setOrderMode,
    history
  ]);

  /**
   * View order button
   */
  const viewOrderButton = React.useCallback(() => {
    if (!match.params.orderId && currentOrder.data) {
      return [
        {
          action: () => {
            history.push(`/orders/${(currentOrder.data as IOrder).id}`);
          },
          newTabPath: `/orders/${(currentOrder.data as IOrder).id}`,
          title: `View ${terminology.order.singular.text}`
        }
      ];
    }

    return [];
  }, [
    currentOrder.data,
    history,
    match.params.orderId,
    terminology.order.singular.text
  ]);

  /**
   * Cancel order button
   */
  const cancelOrderButton = React.useCallback(() => {
    if (checkUserPermission("delete-order")) {
      if (match.params.orderId && currentOrder.data && canBeCancelled === null) {
        const buttonConfig: IAction = {
          action: () => setCancelOrderModal((currentOrder.data as IOrder).id),
          title: `Cancel ${terminology.order.singular.text}`,
          disabled: isViewOnly
        };

        if (isViewOnly) {
          buttonConfig.toolTip = {
            id: currentOrder.data.id.toString(),
            tip: "This order cannot be cancelled"
          };
        }

        return [ buttonConfig ];
      }
    }

    return [];
  }, [
    checkUserPermission,
    match.params.orderId,
    currentOrder.data,
    canBeCancelled,
    isViewOnly,
    terminology.order.singular.text
  ]);

  const placeOrderFromDraftButton = React.useCallback(() => {
    if (checkUserPermission("create-order")) {
      if (currentOrder.data && match.params.orderId) {
        //if order validation returns a string, order is not valid.
        const submitOrderDisabled = typeof canBeSubmitted === "string" || typeof canBeAmended === "string";

        // Fetch Ids for DUE group order statuses
        const allowedToPlaceOrderOrderStatusIds = globalSettings?.orderStatuses
          .filter(orderStatus => (orderStatus.settings.group === "DRAFT" || orderStatus.settings.group === "SMART"))
          .map(orderStatus => orderStatus.id);

        if (!submitOrderDisabled && allowedToPlaceOrderOrderStatusIds?.includes(currentOrder.data.orderStatusId)) {
          return (
            [
              {
                action: () => {
                  submitOrder();
                },
                title: `Place ${terminology.order.singular.text}`
              }
            ]
          );
        }
      }
    }

    return [];
  }, [
    checkUserPermission,
    currentOrder.data,
    match.params.orderId,
    canBeSubmitted,
    canBeAmended,
    globalSettings?.orderStatuses,
    terminology.order.singular.text,
    submitOrder
  ]);

  /**
   * Mark order as manually processed button
   */
  const markOrderAsManuallyProcessedButton = React.useCallback(() => {
    if (checkUserPermission("amend-order")) {
      let statusId: number | null = null;
      let statusName: string | null = null;

      // If there is no globalSettings.orderStatuses return with an empty array
      if (!globalSettings || !globalSettings.orderStatuses) {
        return [];
      }

      // Set the statusId
      statusId = getOrderStatusIdFromCode(globalSettings.orderStatuses, "manually_processed");
      // Set the statusName
      statusName = getOrderStatusNameFromCode(globalSettings.orderStatuses, "manually_processed");

      // If there is no statusName return with an empty array
      if (
        (!match.params.orderId && !statusId) ||
      !statusName ||
      !currentOrder.data ||
      canMarkOrderAsManuallyProcessed(currentOrder.data, role, erp) !== null
      ) {
        return [];
      }

      return [
        {
          title: `Mark as ${statusName.toLowerCase()}`,
          action: () => setShowMarkOrderAsManuallyProcessedModal(true)
        }
      ];
    }

    return [];
  }, [
    checkUserPermission,
    globalSettings,
    match.params.orderId,
    currentOrder.data,
    role,
    erp
  ]);

  /**
   * Re-sync order button
   */
  const reSyncOrderButton = React.useCallback(() => {
    if (checkUserPermission("amend-order") && currentRole?.type === "admin") {
      if (erp === ERPRJL && order || currentOrder.data && canResyncOrder(order || currentOrder.data, role, erp) === null) {
        return [
          {
            action: () => resyncOrder(),
            title: `Resync ${terminology.order.singular.text}`
          }
        ];
      }
    }

    return [];
  }, [
    checkUserPermission,
    order,
    currentOrder.data,
    role,
    erp,
    terminology.order.singular.text,
    resyncOrder,
    currentRole
  ]);

  // Set the action button items array
  actionButtonItems = [
    ...placeOrderFromDraftButton(),
    ...viewOrderButton(),
    ...amendOrderButton(),
    ...cancelOrderButton(),
    ...markOrderAsManuallyProcessedButton(),
    ...reSyncOrderButton(),
    ...createListFromOrderButton(),
    ...duplicateOrderButton(),
    ...updateInvoiceNumberButton()
  ];

  return (
    <>
      <ActionButton
        title={`${terminology.order.singular.text} actions`}
        items={actionButtonItems}
        disabled={actionButtonItems.length === 0}
        loading={currentOrder.data ? !!isSubmittingOrder : undefined}
      />

      <ListFromOrderModal
        show={listFromOrderModal ? true : false}
        handleDismiss={() => setListFromOrderModal(null)}
        onConfirm={listName => {
          if (listFromOrderModal) {
            createListFromOrder({
              id: listFromOrderModal,
              listName
            });
          }
        }}
        terminology={terminology}
      />

      {canAddInvoiceNumber && currentOrder.data && (
        <AddInvoiceNumberModal
          terminology={terminology}
          show={showAddInvoiceNumberModal}
          loading={invoiceOrderResponse.pending}
          initialValue={currentOrder?.data?.invoiceNumber}
          onConfirm={(invoiceNumber: string) => {
            if (!currentOrder.data) {
              toast.error(`${_.capitalize(terminology.order.singular.text)} id not found`);

              return;
            }

            invoiceOrderRequest({
              id: currentOrder.data.id,
              invoiceNumber
            });
          }}
          closeModal={() => setShowAddInvoiceNumberModal(false)}
        />
      )}

      {canDuplicate && currentOrder.data && (
        <DuplicateOrderModal
          terminology={terminology}
          order={currentOrder.data}
          show={showDuplicateOrderModal}
          closeModal={() => setShowDuplicateOrderModal(false)}
        />
      )}

      {currentUser && currentRole && globalSettings && currentOrder.data && (
        <>
          <CancelOrderModal
            order={currentOrder.data as IOrder}
            show={cancelOrderModal ? true : false}
            handleDismiss={() => {
              setCancelOrderModal(null);
              // reload order so updated status displays correctly
              refreshOrder();
            }}
            loading={cancelOrderResponse.pending}
            onConfirm= {() => {
              currentOrder?.data ?
                cancelOrderRequest(currentOrder.data.id) :
                toast.error("Order id not found");
            }}
            terminology={terminology}
            success={cancelOrderResponse.data}
            failure={cancelOrderResponse?.error?.message}
          />

          <OrderAdminMarkAsManuallyProcessedModal
            terminology={terminology}
            statusName={manuallyProcessedStatusName || "Manually processed"}
            show={showMarkOrderAsManuallyProcessedModal}
            onConfirm={note => {
              const strippedNote = stripHtml(note);
              const orderStatusId = getOrderStatusIdFromCode(globalSettings.orderStatuses, "manually_processed");
              const orderNodes = (currentOrder.data as IOrder).orderNotes || [];

              const orderNote: IOrderNote = {
                orderId: (currentOrder.data as IOrder).id,
                body: note,
                authorId: currentUser.id,
                source: OrderNoteSourceEnum.STAFF
              };

              // Error if the strippedNote is not 3 or more characters in length
              if (!strippedNote || strippedNote.length < 3) {
                toast.error(`You must provide a note when setting the order as ${manuallyProcessedStatusName || "Manually processed"}`);

                return;
              }

              if (orderStatusId !== null) {
                // Close the modal
                setShowMarkOrderAsManuallyProcessedModal(false);

                // Save the current order
                markAsManuallyProcessed({
                  id: (currentOrder.data as IOrder).id,
                  orderStatusId,
                  orderNotes: orderNodes.concat([ orderNote ])
                });
              }
            }}
            handleDismiss={() => setShowMarkOrderAsManuallyProcessedModal(false)}
          />
        </>
      )}
    </>
  );
};
